import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, Star, Trash2, Maximize2, FileText, Loader2 } from "lucide-react";
import api, { apiErr } from "../lib/api";
import SummaryModal from "../components/SummaryModal";
import { Input } from "../components/ui/input";
import { useNavigate } from "react-router-dom";

const sentimentDot = { positive: "bg-emerald-400", neutral: "bg-sky-400", negative: "bg-rose-400" };

export default function Favorites() {
  const navigate = useNavigate();
  const [favs, setFavs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [topic, setTopic] = useState("all");
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/favorites");
      setFavs(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const topics = useMemo(() => ["all", ...Array.from(new Set(favs.map((f) => f.topic).filter(Boolean)))], [favs]);

  const filtered = favs.filter((f) => {
    const matchTopic = topic === "all" || f.topic === topic;
    const q = query.toLowerCase();
    const matchQuery = !q || f.headline?.toLowerCase().includes(q) || f.summary_short?.toLowerCase().includes(q);
    return matchTopic && matchQuery;
  });

  const remove = async (id) => {
    try {
      await api.delete(`/favorites/${id}`);
      setFavs((s) => s.filter((f) => f.clip_id !== id));
      toast("Removed from favorites");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-amber-400 mb-2">saved for later</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-50">Favorites</h1>
        </div>
        <Link
          to="/export"
          data-testid="favorites-to-studio-link"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#06231a] font-semibold transition-colors"
        >
          <FileText className="w-4 h-4" /> Open Studio
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            data-testid="favorites-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your saved clips…"
            className="pl-10 bg-[#121721] border-white/10 text-zinc-100 h-11 rounded-xl focus-visible:ring-emerald-500"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="favorites-topic-filter">
          {topics.map((t) => (
            <button
              key={t}
              onClick={() => setTopic(t)}
              className={`shrink-0 px-4 h-11 rounded-xl text-sm font-medium transition-colors border ${
                topic === t
                  ? "bg-emerald-500 text-[#06231a] border-emerald-500"
                  : "bg-[#121721] text-zinc-400 border-white/10 hover:text-zinc-100 hover:bg-white/5"
              }`}
            >
              {t === "all" ? "All" : t}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-72 grid place-items-center"><Loader2 className="w-7 h-7 text-emerald-400 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#121721] border border-white/10 rounded-3xl p-14 text-center max-w-lg mx-auto">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 grid place-items-center mx-auto mb-6">
            <Star className="w-8 h-8 text-amber-400" />
          </div>
          <h2 className="font-serif text-2xl font-bold text-zinc-50 mb-2">No favorites yet</h2>
          <p className="text-zinc-400">Swipe right or tap the star on any clip to save it here.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="favorites-grid">
          {filtered.map((f) => (
            <motion.div
              key={f.clip_id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#121721] border border-white/10 rounded-2xl p-5 flex flex-col hover:border-emerald-500/30 transition-colors"
              data-testid="favorite-card"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase font-mono tracking-widest text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-800/40">{f.topic}</span>
                <span className={`w-1.5 h-1.5 rounded-full ${sentimentDot[f.sentiment] || sentimentDot.neutral}`} />
              </div>
              <h3 className="font-serif text-lg font-bold leading-snug text-zinc-50 mb-2">{f.headline}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed line-clamp-3 mb-4">{f.summary_short}</p>
              <div className="mt-auto flex items-center gap-2">
                <button
                  onClick={() => setActive(f)}
                  data-testid="favorite-expand-button"
                  className="flex-1 h-9 rounded-lg bg-[#1A212E] border border-white/10 text-zinc-200 text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-white/5 transition-colors"
                >
                  <Maximize2 className="w-3.5 h-3.5" /> Read
                </button>
                <button
                  onClick={() => remove(f.clip_id)}
                  data-testid="favorite-remove-button"
                  className="w-9 h-9 rounded-lg border border-white/10 text-zinc-500 grid place-items-center hover:text-rose-300 hover:border-rose-500/40 transition-colors"
                  aria-label="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <SummaryModal
        clip={active}
        open={!!active}
        onClose={() => setActive(null)}
        isFavorite={true}
        onToggleFavorite={(c) => { remove(c.clip_id); setActive(null); }}
        onSendToStudio={() => navigate("/export")}
      />
    </div>
  );
}
